import type { RiskAssessment } from "../lib/scoring";

interface Props {
  risk: RiskAssessment;
  label?: string;
  sub?: string;
}

// Half-circle gauge with colored arc bands and a needle pointing to the score.
// Bands: 0-25 LOW (good), 25-50 MEDIUM (info), 50-75 HIGH (warning), 75-100 CRITICAL (red).
export default function RiskGauge({ risk, label = "Composite risk", sub }: Props) {
  const W = 280, H = 170;
  const cx = W / 2, cy = H - 30;
  const r = 100;

  const arcPath = (start: number, end: number) => {
    const aS = Math.PI * (1 - start / 100);
    const aE = Math.PI * (1 - end / 100);
    const x1 = cx + r * Math.cos(aS);
    const y1 = cy - r * Math.sin(aS);
    const x2 = cx + r * Math.cos(aE);
    const y2 = cy - r * Math.sin(aE);
    return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
  };

  const needleAngle = Math.PI * (1 - risk.score / 100);
  const needleX = cx + (r - 8) * Math.cos(needleAngle);
  const needleY = cy - (r - 8) * Math.sin(needleAngle);

  const bandColor = (b: string) =>
    b === "critical" ? "var(--critical)" :
    b === "high" ? "var(--warning)" :
    b === "medium" ? "var(--info)" :
    "var(--good)";

  return (
    <div className="risk-gauge">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "auto", maxHeight: 180 }}>
        {/* Bands */}
        <path d={arcPath(0, 25)} stroke="var(--good)" strokeWidth={14} fill="none" strokeLinecap="butt" opacity={0.85} />
        <path d={arcPath(25, 50)} stroke="var(--info)" strokeWidth={14} fill="none" strokeLinecap="butt" opacity={0.85} />
        <path d={arcPath(50, 75)} stroke="var(--warning)" strokeWidth={14} fill="none" strokeLinecap="butt" opacity={0.9} />
        <path d={arcPath(75, 100)} stroke="var(--critical)" strokeWidth={14} fill="none" strokeLinecap="butt" opacity={0.92} />

        {/* Tick labels */}
        {[0, 25, 50, 75, 100].map((p) => {
          const a = Math.PI * (1 - p / 100);
          const tx = cx + (r + 14) * Math.cos(a);
          const ty = cy - (r + 14) * Math.sin(a);
          return (
            <text key={p} x={tx} y={ty + 3} textAnchor="middle" fontSize="10" fill="var(--t4)" fontFamily="var(--mono)">
              {p}
            </text>
          );
        })}

        {/* Needle */}
        <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke={bandColor(risk.band)} strokeWidth={3} strokeLinecap="round" />
        <circle cx={cx} cy={cy} r={8} fill={bandColor(risk.band)} />
        <circle cx={cx} cy={cy} r={4} fill="var(--bg-1)" />

        {/* Score readout */}
        <text x={cx} y={cy - 24} textAnchor="middle" fontSize="32" fontWeight="700" fill="var(--t1)" fontFamily="var(--mono)">
          {risk.score}
        </text>
      </svg>
      <div className="risk-gauge-foot">
        <span className={`risk-pill risk-${risk.band}`}>
          <span className="risk-dot" />
          {risk.bandLabel}
        </span>
        <div className="risk-gauge-meta">
          <div className="risk-gauge-label">{label}</div>
          {sub && <div className="risk-gauge-sub">{sub}</div>}
        </div>
      </div>
    </div>
  );
}
